import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/use-user-role";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { formatTime } from "@/lib/utils";
import { format, parseISO } from "date-fns";
import { 
  ArrowLeft, 
  DollarSign, 
  Calendar, 
  XCircle, 
  CheckCircle, 
  Clock,
  TrendingUp,
  Users
} from "lucide-react";

interface BookingData {
  id: string;
  booking_datetime: string;
  booking_timezone?: string;
  price: number;
  status: string;
  payment_status: string;
  created_at: string;
  user_id: string;
  coach_id: string | null;
  cancelled_at: string | null;
  cancellation_fee: number | null;
  profiles?: {
    full_name: string;
    email: string;
  };
}

interface AnalyticsStats {
  totalRevenue: number;
  totalBookings: number;
  confirmedBookings: number;
  deniedBookings: number;
  pendingBookings: number;
  cancelledBookings: number;
  averageBookingValue: number;
  totalCancellationFees: number;
}

const AdminAnalytics = () => {
  const navigate = useNavigate();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const [bookings, setBookings] = useState<BookingData[]>([]);
  const [stats, setStats] = useState<AnalyticsStats>({
    totalRevenue: 0,
    totalBookings: 0,
    confirmedBookings: 0,
    deniedBookings: 0,
    pendingBookings: 0,
    cancelledBookings: 0,
    averageBookingValue: 0,
    totalCancellationFees: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!roleLoading && !isAdmin) {
      navigate("/");
    }
  }, [isAdmin, roleLoading, navigate]);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const { data, error } = await supabase
        .from("bookings")
        .select(`
          *,
          profiles!bookings_user_id_fkey (
            full_name,
            email
          )
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const bookingsData = data || [];
      setBookings(bookingsData);

      // Calculate statistics
      const confirmedBookings = bookingsData.filter(b => b.status === "confirmed");
      const deniedBookings = bookingsData.filter(b => b.status === "denied");
      const pendingBookings = bookingsData.filter(b => b.status === "pending");
      const cancelledBookings = bookingsData.filter(b => b.cancelled_at !== null);

      const totalRevenue = confirmedBookings.reduce((sum, b) => sum + (Number(b.price) || 0), 0);
      const totalCancellationFees = cancelledBookings.reduce((sum, b) => sum + (Number(b.cancellation_fee) || 0), 0);
      const averageBookingValue = confirmedBookings.length > 0 
        ? totalRevenue / confirmedBookings.length 
        : 0;

      setStats({
        totalRevenue,
        totalBookings: bookingsData.length,
        confirmedBookings: confirmedBookings.length,
        deniedBookings: deniedBookings.length,
        pendingBookings: pendingBookings.length,
        cancelledBookings: cancelledBookings.length,
        averageBookingValue,
        totalCancellationFees,
      });
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: "default" | "secondary" | "destructive" | "outline", icon: any }> = {
      pending: { variant: "outline", icon: Clock },
      confirmed: { variant: "default", icon: CheckCircle },
      denied: { variant: "destructive", icon: XCircle },
      proposed: { variant: "secondary", icon: Calendar },
    };

    const config = variants[status] || variants.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  if (roleLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30">
      <nav className="border-b border-border/40 backdrop-blur-sm bg-background/80 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/admin")} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Admin Portal
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-2">
            Analytics Dashboard
          </h1>
          <p className="text-muted-foreground">Track bookings, revenue, and platform performance</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
              <DollarSign className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${stats.totalRevenue.toFixed(2)}</div>
              <p className="text-xs text-muted-foreground mt-1">
                From {stats.confirmedBookings} confirmed bookings
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Bookings</CardTitle>
              <Calendar className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalBookings}</div>
              <p className="text-xs text-muted-foreground mt-1">
                All time bookings
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Average Booking</CardTitle>
              <TrendingUp className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${stats.averageBookingValue.toFixed(2)}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Per confirmed booking
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Pending</CardTitle>
              <Clock className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.pendingBookings}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Awaiting coach response
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Status Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                Confirmed
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.confirmedBookings}</div>
              <p className="text-sm text-muted-foreground mt-1">
                {stats.totalBookings > 0 
                  ? ((stats.confirmedBookings / stats.totalBookings) * 100).toFixed(1)
                  : 0}% of total bookings
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-500" />
                Denied
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.deniedBookings}</div>
              <p className="text-sm text-muted-foreground mt-1">
                {stats.totalBookings > 0 
                  ? ((stats.deniedBookings / stats.totalBookings) * 100).toFixed(1)
                  : 0}% of total bookings
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-orange-500" />
                Cancelled
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.cancelledBookings}</div>
              <p className="text-sm text-muted-foreground mt-1">
                ${stats.totalCancellationFees.toFixed(2)} in fees collected
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Booking History Table */}
        <Card>
          <CardHeader>
            <CardTitle>Booking History</CardTitle>
            <CardDescription>Complete history of all bookings on the platform</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      No bookings found
                    </TableCell>
                  </TableRow>
                ) : (
                  bookings.map((booking) => (
                    <TableRow key={booking.id}>
                      <TableCell>{format(new Date(booking.booking_datetime), "MM/dd/yyyy")}</TableCell>
                      <TableCell>{format(new Date(booking.booking_datetime), "h:mm a")}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{booking.profiles?.full_name || "N/A"}</p>
                          <p className="text-xs text-muted-foreground">{booking.profiles?.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {booking.status === 'cancelled' ? (
                          <div>
                            <p className="font-medium text-destructive">
                              ${(booking.cancellation_fee || 0).toFixed(2)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {booking.cancellation_fee && booking.cancellation_fee > 0 
                                ? "Cancellation fee" 
                                : "Fully refunded"}
                            </p>
                          </div>
                        ) : (
                          <p className="font-medium">${Number(booking.price).toFixed(2)}</p>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(booking.status)}</TableCell>
                      <TableCell>
                        <Badge variant={booking.payment_status === "succeeded" ? "default" : "outline"}>
                          {booking.payment_status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {format(parseISO(booking.created_at), "MM/dd/yyyy")}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminAnalytics;
